import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RedesignCases } from "../../types/RedesignCases";
import api from "../../utils/api";

interface SearchState {
  combined: any[];
  filteredUsers: any[];
  searchedRedesigns: Record<string, RedesignCases[]>;
  searchedCaseUnits: {
    data: any[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
  };

  loading: {
    combined: boolean;
    filteredUsers: boolean;
    searchedRedesigns: boolean;
    searchedCaseUnits: boolean;
  };

  error: {
    combined: string | null;
    filteredUsers: string | null;
    searchedRedesigns: string | null;
    searchedCaseUnits: string | null;
  };
}

const initialState: SearchState = {
  combined: [],
  filteredUsers: [],
  searchedRedesigns: {},
  searchedCaseUnits: {
    data: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 0,
  },
  loading: {
    combined: false,
    filteredUsers: false,
    searchedRedesigns: false,
    searchedCaseUnits: false,
  },
  error: {
    combined: null,
    filteredUsers: null,
    searchedRedesigns: null,
    searchedCaseUnits: null,
  },
};

// -------------------- Thunks --------------------

export const fetchSearchCasesByCaseIdAndCaseOwnerAndStatus = createAsyncThunk(
  "search/fetchSearchCasesByCaseIdAndStatus",
  async (
    { caseOwner, status }: { caseOwner?: string; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/api/search/by-case-id-case-owner-and-status`,
        {
          params: {
            ...(caseOwner ? { caseOwner } : {}),
            ...(status ? { status } : {}),
          },
        }
      );
      return res.data.data.cases || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch filtered cases"
      );
    }
  }
);

export const fetchSearchCasesByCaseIdAndCaseOwnerAndUserId = createAsyncThunk(
  "search/fetchSearchCasesByCaseIdAndCaseOwnerAndUserId",
  async (
    {
      caseOwner,
      userId,
      limit,
      lastCreationTime,
    }: {
      caseOwner?: string;
      userId?: number | string;
      limit?: number;
      lastCreationTime?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/api/search/by-case-id-case-owner-and-user-id`,
        {
          params: {
            ...(caseOwner ? { caseOwner } : {}),
            ...(userId ? { userId } : {}),
            ...(limit ? { limit } : {}),
            ...(lastCreationTime ? { lastCreationTime } : {}),
          },
        }
      );

      return res.data.data || { cases: [], nextCursor: null };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error ||
          "Failed to fetch cases by caseOwner and userId"
      );
    }
  }
);

export const fetchsearchByRedesignCaseIdAndStatus = createAsyncThunk(
  "search/fetchsearchByRedesignCaseIdAndStatus",
  async (
    { caseId, status }: { caseId?: string; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/api/search/by-redesign-case-id-and-status`,
        {
          params: {
            ...(caseId ? { caseId } : {}),
            ...(status ? { status } : {}),
          },
        }
      );

      // The backend returns data.data.data (grouped)
      return res.data.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch filtered redesign cases"
      );
    }
  }
);

export const fetchSearchCaseUnitsByCaseId = createAsyncThunk(
  "search/fetchSearchCaseUnitsByCaseId",
  async (
    {
      caseId,
      page = 1,
      limit = 25,
    }: {
      caseId: string;
      page?: number;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/api/search/by-case-units-case-id`, {
        params: {
          caseId,
          page,
          limit,
        },
      });

      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch filtered case units"
      );
    }
  }
);

export const fetchFilteredUsersByTokenAndRole = createAsyncThunk(
  "search/fetchFilteredUsersByTokenAndRole",
  async (
    { token, role }: { token?: string; role?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/api/search/by-user-token-and-role`,
        {
          params: {
            ...(token ? { token } : {}),
            ...(role ? { role } : {}),
          },
        }
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch filtered users"
      );
    }
  }
);

export const fetchFilteredUsersByEmailAndRole = createAsyncThunk(
  "search/fetchFilteredUsersByEmailAndRole",
  async (
    {
      email,
      role,
      token,
      portalAccess,
    }: {
      email?: string;
      role?: string;
      token?: string;
      portalAccess?: "" | "smile" | "cases";
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(
        `/api/search/by-user-email-and-role`,
        {
          params: {
            ...(email ? { email } : {}),
            ...(role ? { role } : {}),
            ...(token ? { token } : {}),
            ...(portalAccess ? { portalAccess } : {}),
          },
        }
      );

      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch filtered users"
      );
    }
  }
);
// -------------------- Slice --------------------

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    resetCombinedState: (state) => {
      state.combined = [];
      state.loading.combined = false;
      state.error.combined = null;
    },
    resetFilteredUsersState: (state) => {
      state.filteredUsers = [];
      state.loading.filteredUsers = false;
      state.error.filteredUsers = null;
    },
    resetSearchedRedesignsState: (state) => {
      state.searchedRedesigns = {};
      state.loading.searchedRedesigns = false;
      state.error.searchedRedesigns = null;
    },
    resetSearchedCaseUnitsState: (state) => {
      state.searchedCaseUnits = {
        data: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 0,
      };
      state.loading.searchedCaseUnits = false;
      state.error.searchedCaseUnits = null;
    },
    resetAllState: (state) => {
      state.combined = [];
      state.filteredUsers = [];
      state.searchedRedesigns = {};
      state.searchedCaseUnits = {
        data: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 0,
      };
      state.loading = {
        combined: false,
        filteredUsers: false,
        searchedRedesigns: false,
        searchedCaseUnits: false,
      };
      state.error = {
        combined: null,
        filteredUsers: null,
        searchedRedesigns: null,
        searchedCaseUnits: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // ---------------- Cases Search ----------------
      .addCase(
        fetchSearchCasesByCaseIdAndCaseOwnerAndStatus.pending,
        (state) => {
          state.loading.combined = true;
          state.error.combined = null;
        }
      )
      .addCase(
        fetchSearchCasesByCaseIdAndCaseOwnerAndStatus.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading.combined = false;
          state.combined = action.payload;
        }
      )
      .addCase(
        fetchSearchCasesByCaseIdAndCaseOwnerAndStatus.rejected,
        (state, action) => {
          state.loading.combined = false;
          state.error.combined = action.payload as string;
        }
      )

      .addCase(
        fetchSearchCasesByCaseIdAndCaseOwnerAndUserId.pending,
        (state) => {
          state.loading.combined = true;
          state.error.combined = null;
        }
      )
      .addCase(
        fetchSearchCasesByCaseIdAndCaseOwnerAndUserId.fulfilled,
        (
          state,
          action: PayloadAction<{ cases: any[]; nextCursor: number | null }>
        ) => {
          state.loading.combined = false;
          state.combined = action.payload.cases;
        }
      )
      .addCase(
        fetchSearchCasesByCaseIdAndCaseOwnerAndUserId.rejected,
        (state, action) => {
          state.loading.combined = false;
          state.error.combined = action.payload as string;
        }
      )

      // ---------------- Redesign Search ----------------
      .addCase(fetchsearchByRedesignCaseIdAndStatus.pending, (state) => {
        state.loading.searchedRedesigns = true;
        state.error.searchedRedesigns = null;
      })
      .addCase(
        fetchsearchByRedesignCaseIdAndStatus.fulfilled,
        (state, action: PayloadAction<Record<string, RedesignCases[]>>) => {
          state.loading.searchedRedesigns = false;
          state.searchedRedesigns = action.payload;
        }
      )
      .addCase(
        fetchsearchByRedesignCaseIdAndStatus.rejected,
        (state, action) => {
          state.loading.searchedRedesigns = false;
          state.error.searchedRedesigns = action.payload as string;
        }
      )

      // ---------------- Case Units Search ----------------
      .addCase(fetchSearchCaseUnitsByCaseId.pending, (state) => {
        state.loading.searchedCaseUnits = true;
        state.error.searchedCaseUnits = null;
      })
      .addCase(fetchSearchCaseUnitsByCaseId.fulfilled, (state, action) => {
        state.loading.searchedCaseUnits = false;
        state.searchedCaseUnits = action.payload;
      })
      .addCase(fetchSearchCaseUnitsByCaseId.rejected, (state, action) => {
        state.loading.searchedCaseUnits = false;
        state.error.searchedCaseUnits = action.payload as string;
      })

      // ---------------- Filtered Users (Token + Role) ----------------
      .addCase(fetchFilteredUsersByTokenAndRole.pending, (state) => {
        state.loading.filteredUsers = true;
        state.error.filteredUsers = null;
      })
      .addCase(
        fetchFilteredUsersByTokenAndRole.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading.filteredUsers = false;
          state.filteredUsers = action.payload;
        }
      )
      .addCase(fetchFilteredUsersByTokenAndRole.rejected, (state, action) => {
        state.loading.filteredUsers = false;
        state.error.filteredUsers = action.payload as string;
      })

      // ---------------- Filtered Users (Email + Role) ----------------
      .addCase(fetchFilteredUsersByEmailAndRole.pending, (state) => {
        state.loading.filteredUsers = true;
        state.error.filteredUsers = null;
      })
      .addCase(
        fetchFilteredUsersByEmailAndRole.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.loading.filteredUsers = false;
          state.filteredUsers = action.payload;
        }
      )
      .addCase(fetchFilteredUsersByEmailAndRole.rejected, (state, action) => {
        state.loading.filteredUsers = false;
        state.error.filteredUsers = action.payload as string;
      });
  },
});

export const {
  resetCombinedState,
  resetFilteredUsersState,
  resetSearchedRedesignsState,
  resetSearchedCaseUnitsState,
  resetAllState,
} = searchSlice.actions;

export default searchSlice.reducer;